import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PlayerProvider } from './contexts/PlayerContext';
import { LibraryProvider } from './contexts/LibraryContext';
import { ProtectedRoute } from './components/auth';
import { Navbar } from './components/layout';
import { Spinner } from './components/common';
import { useDevToolsProtection } from './hooks';
import './i18n/config';
import './App.css';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const BannedPage = lazy(() => import('./pages/BannedPage/BannedPage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage/LibraryPage'));
const GBoxDetailPage = lazy(() => import('./pages/GBoxDetailPage/GBoxDetailPage'));
const PlayerPage = lazy(() => import('./pages/PlayerPage/PlayerPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage/NotFoundPage'));

function LoadingFallback() {
  return (
    <div className="loading-fallback">
      <Spinner size="large" />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const isPlayerPage = location.pathname.startsWith('/player/');
  const isLoginPage = location.pathname === '/login';

  return (
    <div className="app">
      <Navbar />
      <main className={`main-content${isPlayerPage ? ' player-active' : ''}${isLoginPage ? ' login-active' : ''}`}>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/banned" element={<BannedPage />} />

            {/* Protected routes */}
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <LibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gbox/:gboxId"
              element={
                <ProtectedRoute>
                  <GBoxDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/player/:gboxId"
              element={
                <ProtectedRoute>
                  <PlayerPage />
                </ProtectedRoute>
              }
            />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/library" replace />} />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

function App() {
  useDevToolsProtection();

  return (
    <BrowserRouter>
      <AuthProvider>
        <LibraryProvider>
          <PlayerProvider>
            <AppContent />
          </PlayerProvider>
        </LibraryProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
